import React, { useEffect, useMemo, useState } from 'react';
import { errorText } from '../../api/errorText';
import { useV3Api } from '../../api/useV3Api';
import { bodyOf, fieldOfProblem, filterChanged, formOf } from '../../policy/policyModel';
import ShowCommand from '../ShowCommand';
import ActionPreview from './ActionPreview';
import ExceptionField from './ExceptionField';
import FilterField from './FilterField';
import JsonField from './JsonField';
import LimitsField from './LimitsField';
import RulesField from './RulesField';

const ROUTE = 'v3/policies';

// "Patch policy" -> "patch policy", but "CVE exclusion" stays "CVE exclusion".
const lowerFirst = (title) => (/^[A-Z]{2}/.test(title) ? title : title.charAt(0).toLowerCase() + title.slice(1));

// The editor of one policy, built from the kind's description (GET v3/policykinds): a field for each entry, of the type it names.
//   spec      the kind's description
//   doc       the policy being changed, or null for a new one
//   account   the account it belongs to (* for the global layer)
// Save sends the document and shows the API's own words when it refuses it, beside the field they are about.
export default function PolicyEditor({ spec, doc, account, onSaved, onCancel, inline = false }) {
  const { get, put } = useV3Api();
  // A software policy keeps its labels for life, and a new customer one always has account = the account: the pair that ties it to the customer.
  const keepsLabels = spec.kind === 'softwarepolicy';
  const lockedFilter = keepsLabels && !!doc;
  const fixedPairs = keepsLabels && !doc && account && account !== '*' ? 1 : 0;
  const [form, setForm] = useState(() => {
    if (doc) return formOf(spec, doc);
    const blank = formOf(spec, {});
    const named = spec.fields.find((f) => f.type === 'filter');
    return fixedPairs && named ? { ...blank, [named.name]: [{ label: 'account', value: account }] } : blank;
  });
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState({ message: '', field: null });
  const [defaults, setDefaults] = useState(null);        // the action defaults that apply, read for a definition's "What will run"

  useEffect(() => {
    if (spec.kind !== 'definition') return undefined;
    let cancelled = false;
    get(ROUTE, { account, kind: 'actiondefaults' })
      .then((body) => { if (!cancelled) setDefaults((body && body.items) || []); })
      .catch(() => { if (!cancelled) setDefaults([]); });
    return () => { cancelled = true; };
  }, [get, spec.kind, account]);

  const built = useMemo(() => bodyOf(spec, form, account), [spec, form, account]);
  const setField = (name, value) => { setForm((f) => ({ ...f, [name]: value })); if (problem.message) setProblem({ message: '', field: null }); };
  const filterField = spec.fields.find((f) => f.type === 'filter');
  const movedFilter = !!(doc && filterField && filterChanged(doc, form));

  const save = async () => {
    if (built.error) { setProblem({ message: built.error, field: built.field }); return; }
    setSaving(true);
    try {
      const stored = await put(ROUTE, built.doc);
      onSaved(stored);
    } catch (ex) {
      const message = errorText(ex);
      setProblem({ message, field: fieldOfProblem(message, spec) });
    } finally {
      setSaving(false);
    }
  };

  const control = (field) => {
    const value = form[field.name];
    switch (field.type) {
      case 'filter': return <FilterField field={field} value={value} listId={`labels-${spec.kind}`} locked={lockedFilter} fixed={fixedPairs} onChange={(v) => setField(field.name, v)} />;
      case 'limits': return <LimitsField field={field} value={value} onChange={(v) => setField(field.name, v)} />;
      case 'rules': return <RulesField field={field} value={value} account={account} filter={form[filterField ? filterField.name : 'filter']} onChange={(v) => setField(field.name, v)} />;
      case 'exception': return <ExceptionField value={value} required={!!field.required} onChange={(v) => setField(field.name, v)} />;
      case 'json': return <JsonField field={field} value={value} onChange={(v) => setField(field.name, v)} />;
      case 'flag': return (
        <label className="check"><input type="checkbox" aria-label={field.label} checked={!!value} onChange={(e) => setField(field.name, e.target.checked)} />{field.help}</label>
      );
      case 'enum': return (
        <select id={`f-${field.name}`} value={value} onChange={(e) => setField(field.name, e.target.value)}>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      default: return (
        <input type="text" id={`f-${field.name}`} maxLength={field.max} value={value || ''} disabled={!!doc && field.name === 'key'} onChange={(e) => setField(field.name, e.target.value)} />
      );
    }
  };

  return (
    <div className={inline ? 'policy-editor inline' : 'card policy-editor'} role="form" aria-label={`${doc ? 'Change' : 'New'} ${lowerFirst(spec.title)}`}>
      {!inline && (
        <div className="card-head">
          <div>
            <h3>{doc ? 'Change' : 'New'} {lowerFirst(spec.title)}</h3>
            <div className="sub">{account === '*' ? 'Global layer: every customer inherits it unless it has its own.' : `For ${account}. It beats the global layer.`} {spec.description}</div>
          </div>
        </div>
      )}
      <div className={inline ? 'inline-body' : 'card-body'}>
        {problem.message && !problem.field && <div className="field-error" role="alert">{problem.message}</div>}
        {spec.fields.map((field) => (
          <div className="field" key={field.name} data-testid={`field-${field.name}`}>
            {['text', 'enum'].includes(field.type)
              ? <label htmlFor={`f-${field.name}`}>{field.label}{field.required ? ' *' : ''}</label>
              : <div className="label">{field.label}{field.required ? ' *' : ''}</div>}
            {control(field)}
            {spec.kind === 'definition' && field.name === 'actions' && <ActionPreview defaults={defaults} text={form.actions} detectText={form.detect} />}
            {field.help && !['flag', 'limits', 'json'].includes(field.type) && <div className="help">{field.help}</div>}
            {field.type === 'filter' && movedFilter && <div className="help warn">Changing the filter saves a new policy. The original stays until you delete it.</div>}
            {problem.field === field.name && <div className="field-error" role="alert">{problem.message}</div>}
          </div>
        ))}
        <div className="editor-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" className="btn" disabled={saving} onClick={onCancel}>Cancel</button>
          <ShowCommand route={ROUTE} method="PUT" body={built.doc} disabled={!built.doc} label="PowerShell" />
        </div>
      </div>
    </div>
  );
}
